import { useState, useEffect, createContext, useContext, type FC, type ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  needsEmailVerification: boolean;
  pendingVerificationEmail: string | null;
  setPendingVerificationEmail: (email: string | null) => void;
  signUp: (email: string, password: string, firstName?: string, lastName?: string, isFoundingHost?: boolean) => Promise<{ error: any; needsRedirect?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: any; needsVerification?: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  signInWithProvider: (provider: 'google' | 'apple', redirectPath?: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Check if user exists but is not email confirmed
        if (session?.user && !session.user.email_confirmed_at) {
          setNeedsEmailVerification(true);
        } else {
          setNeedsEmailVerification(false);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // Check if user exists but is not email confirmed
      if (session?.user && !session.user.email_confirmed_at) {
        setNeedsEmailVerification(true);
      } else {
        setNeedsEmailVerification(false);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string, isFoundingHost?: boolean) => {
    try {
      if (!email || !password) {
        toast({
          title: "Fehler",
          description: "E-Mail und Passwort sind erforderlich.",
          variant: "destructive",
        });
        return { error: new Error('Email and password required') };
      }

      // First, cleanup any old unverified accounts with this email (>5 minutes old)
      const { error: cleanupError } = await supabase.rpc('cleanup_old_unverified_accounts', {
        p_email: email.trim().toLowerCase()
      });

      if (cleanupError) {
        console.error('Error cleaning up old accounts:', cleanupError);
      }

      // Include founding flag in redirect URL if applicable
      const redirectUrl = isFoundingHost 
        ? `${window.location.origin}/email-confirmed?founding=true`
        : `${window.location.origin}/email-confirmed`;
      
      const { error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName || undefined,
            last_name: lastName || undefined,
            is_founding_signup: isFoundingHost || false,
          }
        }
      });

      if (error) {
        let errorMessage = 'Ein Fehler ist bei der Registrierung aufgetreten';
        let isRateLimit = false;
        
        if (error.message.includes('rate') || error.message.includes('429') || error.message.includes('security purposes')) {
          errorMessage = 'Die Bestätigungs-E-Mail wurde bereits gesendet. Bitte prüfe dein Postfach (auch Spam-Ordner) und versuche es in einer Minute erneut.';
          isRateLimit = true;
        } else if (error.message.includes('already registered')) {
          errorMessage = 'Diese E-Mail-Adresse ist bereits mit einem bestätigten Konto verknüpft. Bitte melde dich an oder verwende eine andere E-Mail-Adresse.';
        }
        
        toast({
          title: isRateLimit ? "E-Mail bereits gesendet" : "Registrierung fehlgeschlagen",
          description: errorMessage,
          variant: isRateLimit ? "default" : "destructive",
        });
        
        // For rate limits, still redirect since the email was already sent
        if (isRateLimit) {
          setNeedsEmailVerification(true);
          return { error: null, needsRedirect: true };
        }
      } else {
        setNeedsEmailVerification(true);
        // Don't show toast here, the EmailVerificationPending page will handle it
      }

      return { error, needsRedirect: !error };
    } catch (error: any) {
      toast({
        title: "Registrierung fehlgeschlagen",
        description: "Ein unerwarteter Fehler ist aufgetreten",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signInWithProvider = async (provider: 'google' | 'apple', redirectPath?: string) => {
    try {
      console.log('OAuth-Anmeldung gestartet für Provider:', provider);
      
      // Build redirect URL, preserving any post-auth destination
      const baseUrl = window.location.origin;
      const redirectTo = redirectPath 
        ? (redirectPath.startsWith('/') ? `${baseUrl}${redirectPath}` : redirectPath)
        : `${baseUrl}/`;
      
      console.log('OAuth redirectTo:', redirectTo);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
        }
      });

      if (error) {
        console.error('OAuth Fehler:', error);
        let errorMessage = `Fehler bei der ${provider === 'google' ? 'Google' : 'Apple'}-Anmeldung`;
        
        if (error.message.includes('not authorized')) {
          errorMessage = 'OAuth-Provider ist nicht konfiguriert oder aktiviert';
        } else if (error.message.includes('redirect')) {
          errorMessage = 'Redirect-URL ist nicht korrekt konfiguriert';
        }
        
        toast({
          title: "Anmeldung fehlungschlagen",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        console.log('OAuth-Anmeldung erfolgreich gestartet');
      }

      return { error };
    } catch (error: any) {
      console.error('OAuth unerwarteter Fehler:', error);
      toast({
        title: "Anmeldung fehlgeschlagen",
        description: "Ein unerwarteter Fehler ist aufgetreten",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 Login-Versuch gestartet für:', email);
      
      if (!email || !password) {
        console.error('❌ Login fehlgeschlagen: Email oder Passwort fehlt');
        toast({
          title: "Fehler",
          description: "E-Mail und Passwort sind erforderlich.",
          variant: "destructive",
        });
        return { error: new Error('Email and password required') };
      }

      const { error, data } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      console.log('🔐 Login-Antwort erhalten:', { 
        hasError: !!error, 
        hasUser: !!data?.user,
        emailConfirmed: data?.user?.email_confirmed_at,
        errorMessage: error?.message 
      });

      if (error) {
        console.error('❌ Login-Fehler:', error);
        let errorMessage = 'Ungültige Anmeldedaten';
        let needsVerification = false;
        
        if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Bitte bestätige deine E-Mail-Adresse, bevor du dich anmeldest.';
          needsVerification = true;
          setPendingVerificationEmail(email.trim().toLowerCase());
          setNeedsEmailVerification(true);
        } else if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'E-Mail oder Passwort ist falsch';
        }
        
        toast({
          title: "Anmeldung fehlgeschlagen",
          description: errorMessage,
          variant: "destructive",
        });
        
        return { error, needsVerification };
      } else {
        // Check if email is verified (double-check after successful auth)
        if (data.user && !data.user.email_confirmed_at) {
          console.warn('⚠️ Login blockiert: E-Mail nicht bestätigt');
          setNeedsEmailVerification(true);
          setPendingVerificationEmail(email.trim().toLowerCase());
          await supabase.auth.signOut();
          return { error: { message: "Email not confirmed" }, needsVerification: true };
        }
        
        console.log('✅ Login erfolgreich für User:', data.user?.id);
        toast({
          title: "Erfolgreich angemeldet",
          description: "Willkommen zurück!",
        });
      }

      return { error, needsVerification: false };
    } catch (error: any) {
      console.error('❌ Unerwarteter Login-Fehler:', error);
      toast({
        title: "Anmeldung fehlgeschlagen",
        description: "Ein unerwarteter Fehler ist aufgetreten",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        toast({
          title: "Abmeldung fehlgeschlagen",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erfolgreich abgemeldet",
          description: "Auf Wiedersehen!",
        });
      }
    } catch (error: any) {
      toast({
        title: "Abmeldung fehlgeschlagen",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      if (!email) {
        toast({
          title: "Ungültige E-Mail",
          description: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
          variant: "destructive",
        });
        return { error: new Error('Invalid email') };
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/password-reset`,
      });

      if (error) {
        toast({
          title: "Fehler beim Zurücksetzen",
          description: "Fehler beim Senden der E-Mail. Versuchen Sie es später erneut.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "E-Mail gesendet",
          description: "Bitte prüfen Sie Ihre E-Mail für weitere Anweisungen.",
        });
      }

      return { error };
    } catch (error: any) {
      toast({
        title: "Fehler beim Zurücksetzen",
        description: "Ein unerwarteter Fehler ist aufgetreten",
        variant: "destructive",
      });
      return { error };
    }
  };

  const value = {
    user,
    session,
    loading,
    needsEmailVerification,
    pendingVerificationEmail,
    setPendingVerificationEmail,
    signUp,
    signIn,
    signOut,
    resetPassword,
    signInWithProvider,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}